import type { AiCompanionAttendanceSessionUser } from "@simbelapp/order-sdk";
import { AttendanceUserGroupEnum } from "~/utils/enums/attendance-review.enums";
import type { IAttendanceParticipantOption } from "~/utils/interfaces/attendance-review-interfaces";

/** How many collaborators the "Tous les utilisateurs" group asks the API for at a time. */
export const ATTENDANCE_USER_PAGE_SIZE = 20;

/** Headers of the participant picker, in the order the design lists its groups. */
export const ATTENDANCE_USER_GROUP_LABELS: Record<AttendanceUserGroupEnum, string> = {
  [AttendanceUserGroupEnum.SESSION]: "Inscrits à cette session",
  [AttendanceUserGroupEnum.COMPANY]: "Tous les utilisateurs",
};

/**
 * Case, accent and punctuation are all noise when comparing a name typed on a platform with the
 * same name handwritten on a sheet — only a real spelling difference should raise a flag.
 */
export function normalizeName(name: string): string {
  return (
    name
      .normalize("NFD")
      // Combining marks left behind by NFD — written as escapes so they stay visible in source.
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim()
  );
}

/** The session's roster, as the two picker groups and the enrolment check both want it. */
export function toParticipantOptions(users: AiCompanionAttendanceSessionUser[]): IAttendanceParticipantOption[] {
  return users.map((user) => ({ userId: user.userId, displayValue: user.fullname }));
}

/**
 * The picker filters its session group itself: the company group is searched server-side, on more
 * fields than a name (email, matricule), so letting the dropdown re-filter would throw those away.
 */
export function filterParticipants(
  users: IAttendanceParticipantOption[],
  search: string,
): IAttendanceParticipantOption[] {
  const needle = normalizeName(search);
  if (!needle) return users;

  return users.filter((user) => normalizeName(user.displayValue).includes(needle));
}
